import React, { useEffect, useMemo, useState } from "react";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import BusyOverlay from "../components/common/BusyOverlay";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import type { CertificateApplication } from "../types/certificate.types";
import {
  downloadReportPdf,
  printReportPdf,
  getReportPdfBase64,
} from "../utils/reportPdf";
import EmailReportModal from "../components/common/EmailReportModal";
import "../styles/pages/Reports.css";

const getLast90Days = () => {
  const date = new Date();
  date.setDate(date.getDate() - 90);
  return date.toISOString().split("T")[0];
};

const getToday = () => new Date().toISOString().split("T")[0];

const dayDiff = (start?: string, end?: string) => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
    return null;
  return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
};

const ProcessingTimelinessReport: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applications, setApplications] = useState<CertificateApplication[]>(
    [],
  );
  const [filters, setFilters] = useState({
    startDate: getLast90Days(),
    endDate: getToday(),
    category: "",
    county: "",
    processType: "all",
  });

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Processing Timeliness Report",
      "Measure application processing speed and backlog",
    );
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await certificateService.getCertificateApplications();
        setApplications(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err?.message || "Failed to load timeliness report");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [setPageTitle]);

  const countyOptions = useMemo(
    () =>
      [
        ...new Set(
          applications
            .map((item) => item.countyName || item.county?.name)
            .filter(Boolean) as string[],
        ),
      ].sort(),
    [applications],
  );

  const filteredRows = useMemo(() => {
    return applications.filter((item) => {
      const created = item.createdAt
        ? new Date(item.createdAt).toISOString().split("T")[0]
        : "";
      const matchesDate =
        (!filters.startDate || (created && created >= filters.startDate)) &&
        (!filters.endDate || (created && created <= filters.endDate));

      const matchesCategory =
        !filters.category ||
        (item.category || "").toLowerCase() === filters.category.toLowerCase();
      const county = (item.countyName || item.county?.name || "").toLowerCase();
      const matchesCounty =
        !filters.county || county === filters.county.toLowerCase();

      const isHq = Boolean(item.processAtHeadquarters);
      const matchesProcessType =
        filters.processType === "all" ||
        (filters.processType === "headquarters" && isHq) ||
        (filters.processType === "county" && !isHq);

      return (
        matchesDate && matchesCategory && matchesCounty && matchesProcessType
      );
    });
  }, [applications, filters]);

  const computedRows = useMemo(() => {
    return filteredRows.map((item) => {
      const elapsedDays = dayDiff(
        item.createdAt,
        item.updatedAt || item.createdAt,
      );
      return {
        ...item,
        elapsedDays,
      };
    });
  }, [filteredRows]);

  const metrics = useMemo(() => {
    const withElapsed = computedRows.filter(
      (item) => item.elapsedDays !== null,
    ) as Array<CertificateApplication & { elapsedDays: number }>;
    const averageDays = withElapsed.length
      ? withElapsed.reduce((sum, item) => sum + item.elapsedDays, 0) /
        withElapsed.length
      : 0;

    const pendingCount = computedRows.filter((item) => {
      const status = (item.statusName || item.status?.name || "").toLowerCase();
      return (
        status.includes("pending") ||
        status.includes("query") ||
        status.includes("approval")
      );
    }).length;

    const overSevenDays = withElapsed.filter(
      (item) => item.elapsedDays > 7,
    ).length;

    return {
      total: computedRows.length,
      averageDays,
      pendingCount,
      overSevenDays,
    };
  }, [computedRows]);

  const [showEmailModal, setShowEmailModal] = useState(false);

  const buildPdfConfig = () => {
    const rows = computedRows.map((item) => [
      item.formNo || "-",
      item.category || "-",
      item.countyName || item.county?.name || "-",
      item.statusName || item.status?.name || "-",
      item.processAtHeadquarters ? "Headquarters" : "County",
      item.createdAt ? new Date(item.createdAt).toLocaleString() : "-",
      item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "-",
      item.elapsedDays !== null ? item.elapsedDays.toFixed(1) : "-",
    ]);

    return {
      title: "Processing Timeliness Report",
      filters: [
        { label: "Start Date", value: filters.startDate },
        { label: "End Date", value: filters.endDate },
        { label: "Category", value: filters.category || "All" },
        { label: "County", value: filters.county || "All" },
        { label: "Processing Location", value: filters.processType },
      ],
      columns: [
        "Form No.",
        "Category",
        "County",
        "Status",
        "Location",
        "Created At",
        "Last Updated",
        "Elapsed Days",
      ],
      rows,
    };
  };

  const handleDownloadPdf = () => {
    downloadReportPdf("processing-timeliness-report.pdf", buildPdfConfig());
  };

  const handlePrintPdf = () => {
    printReportPdf(buildPdfConfig());
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="reports-page">
          <BusyOverlay
            isVisible={loading}
            message="Loading processing report..."
          />
          {error ? <div className="alert alert-error">{error}</div> : null}

          <div className="reports-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handlePrintPdf}
              disabled={computedRows.length === 0}
            >
              Print PDF
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleDownloadPdf}
              disabled={computedRows.length === 0}
            >
              Download PDF
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setShowEmailModal(true)}
              disabled={computedRows.length === 0}
            >
              ✉️ Email
            </button>
          </div>

          <EmailReportModal
            open={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            getAttachmentBase64={() => getReportPdfBase64(buildPdfConfig())}
            fileName="processing-timeliness-report.pdf"
            defaultSubject="Processing Timeliness Report"
            defaultMessage="Please find the attached Processing Timeliness Report."
          />

          <div className="reports-filters">
            <div className="filter-group">
              <label className="filter-label">Start Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">End Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Category</label>
              <select
                className="filter-select"
                value={filters.category}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, category: e.target.value }))
                }
              >
                <option value="">All Categories</option>
                <option value="Marriage">Marriage</option>
                <option value="Divorce">Divorce</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">County</label>
              <select
                className="filter-select"
                value={filters.county}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, county: e.target.value }))
                }
              >
                <option value="">All Counties</option>
                {countyOptions.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Processing Location</label>
              <select
                className="filter-select"
                value={filters.processType}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    processType: e.target.value,
                  }))
                }
              >
                <option value="all">All</option>
                <option value="county">County</option>
                <option value="headquarters">Headquarters</option>
              </select>
            </div>
          </div>

          <div className="reports-kpis">
            <div className="kpi-card">
              <span className="kpi-label">Applications in Scope</span>
              <span className="kpi-value">{metrics.total}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Avg. Processing Days</span>
              <span className="kpi-value">
                {metrics.averageDays.toFixed(1)}
              </span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Pending / In Review</span>
              <span className="kpi-value">{metrics.pendingCount}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Over 7 Days</span>
              <span className="kpi-value">{metrics.overSevenDays}</span>
            </div>
          </div>

          <div className="reports-table-container">
            <div className="table-wrapper">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Form No.</th>
                    <th>Category</th>
                    <th>County</th>
                    <th>Status</th>
                    <th>Processing Location</th>
                    <th>Elapsed Days</th>
                  </tr>
                </thead>
                <tbody>
                  {computedRows.map((item) => (
                    <tr key={item.id || `${item.formNo}-${item.createdAt}`}>
                      <td>{item.formNo || "-"}</td>
                      <td>{item.category || "-"}</td>
                      <td>{item.countyName || item.county?.name || "-"}</td>
                      <td>{item.statusName || item.status?.name || "-"}</td>
                      <td>
                        {item.processAtHeadquarters ? "Headquarters" : "County"}
                      </td>
                      <td>
                        {item.elapsedDays !== null
                          ? item.elapsedDays.toFixed(1)
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default ProcessingTimelinessReport;
